import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";
import cartService from "@/apps/shared/buyback/core/domain/service/cartService";
import { BuybackInCart } from "@/apps/shared/buyback/core/domain/model/buybackInCart/BuybackInCart";
import { CustomerInfo } from "@/apps/shared/buyback/core/domain/service/DTO/customerInfo/customerInfo";
import { CustomerIdentification } from "@/apps/shared/buyback/core/domain/service/DTO/customerInfo/customerIdentification";
import { PaymentMethod } from "@/apps/shared/proposal/core/domain/model/PaymentMethod";
import { Cart } from "@/apps/shared/buyback/core/domain/model/Cart";
import cartRepository from "@/apps/shared/buyback/core/infrastructure/repository/cartRepository";
import tempCustomerInfosRepository from "@/apps/dashboard/buyback/core/infrastructure/repository/temporaryCustomerInfosRepository";
import dashboardBuybackSettingsRepository from "@/apps/shared/buyback/core/infrastructure/repository/dashboardBuybackSettingsRepository";
import whiteLabelBuybackSettingsRepository from "@/apps/shared/buyback/core/infrastructure/repository/whiteLabelBuybackSettingsRepository";

export const useCartStore = defineStore("cart", () => {
  const nullableCart = ref<Cart | null>(null);

  const cart: ComputedRef<Cart> = computed(() => {
    if (nullableCart.value === null) {
      throw new Error("Cart is null.");
    }
    return nullableCart.value;
  });

  const getCartTotal: ComputedRef<number> = computed(() => {
    return cart.value.getTotalPriceWithModifiersAsDecimal();
  });

  const getCartBuybacksTotal: ComputedRef<number> = computed(() => {
    return cart.value.getTotalPriceWithoutModifiersAsDecimal();
  });

  const getCartPriceImpactsTotal: ComputedRef<number> = computed(() => {
    return cart.value.getPriceImpactsTotalAsDecimal();
  });

  const isCartEmpty: ComputedRef<boolean> = computed(() => {
    return cart.value.isEmpty();
  });

  const isCartFull: ComputedRef<boolean> = computed(() => {
    return cart.value.isFull();
  });

  const getCartPaymentModes: ComputedRef<Array<PaymentMethod>> = computed(() => {
    return cart.value.getPaymentModes();
  });

  const canAddAnotherProductToCart: ComputedRef<boolean> = computed(() => {
    return cart.value.cartBuybacks.length + 1 < cart.value.maxNbOfItems;
  });

  const initCart = async (isDashboard: boolean): Promise<void> => {
    nullableCart.value = await cartService.getInitialCart(
      isDashboard ? dashboardBuybackSettingsRepository : whiteLabelBuybackSettingsRepository,
      cartRepository
    );
  };

  const resetCart = (): void => {
    cart.value.reset();
    cartRepository.storeCart(cart.value);
    tempCustomerInfosRepository.removeTempCustomerInfos();
  };

  const updateCartWithBuyback = (passedCartBuyback: BuybackInCart): void => {
    if (isCartFull.value) {
      return;
    }
    cart.value.addBuyback(passedCartBuyback);
    cartRepository.storeCart(cart.value);
  };

  const updateCartWithCustomer = (
    customerInfo: CustomerInfo,
    customerIdentification: CustomerIdentification | null
  ): void => {
    cart.value.setCustomerInfo(customerInfo);
    if (customerIdentification !== null) {
      cart.value.setCustomerIdentification(customerIdentification);
    }
    cartRepository.storeCart(cart.value);
  };

  const updateCartWithPreDeclaredBuybackId = (preDeclaredBuybackId: string): void => {
    cart.value.setPreDeclaredBuybackId(preDeclaredBuybackId);
    cartRepository.storeCart(cart.value);
  };

  const removeBuybackFromCart = (passedCartBuybackId: string): void => {
    const thisIsTheLastItem = cart.value.cartBuybacks.length <= 1;
    if (thisIsTheLastItem) {
      resetCart();
      return;
    }

    const indexOfCartBuybackWithPassedId = cart.value.cartBuybacks.findIndex(
      (cb) => cb.inCartId === passedCartBuybackId
    );
    if (indexOfCartBuybackWithPassedId === -1) {
      return;
    }

    cart.value.removeBuyback(indexOfCartBuybackWithPassedId);
    cartRepository.storeCart(cart.value);
  };

  /*
   * Temporary customer infos
   */

  const storeTemporaryCustomerInfos = (
    tempCustomerInfo: CustomerInfo,
    tempCustomerIdentification: CustomerIdentification | null
  ): void => {
    tempCustomerInfosRepository.storeTempCustomerInfos({
      tempCustomerInfo: tempCustomerInfo,
      tempCustomerIdentification: tempCustomerIdentification,
    });
  };

  const loadTempCustomerInfosInCart = (): void => {
    if (false === tempCustomerInfosRepository.hasTemporaryCustomerInfos()) {
      return;
    }

    const { tempCustomerInfo, tempCustomerIdentification } = tempCustomerInfosRepository.getTempCustomerInfos();
    cart.value.setCustomerInfo(tempCustomerInfo);
    if (tempCustomerIdentification !== null) {
      cart.value.setCustomerIdentification(tempCustomerIdentification);
    }
  };

  /*
   Cart visibility
   */

  const show = ref<boolean>(true);

  const toggleCartVisibility = (payload: boolean): void => {
    show.value = payload;
  };

  return {
    // Cart management
    cart,
    getCartTotal,
    getCartBuybacksTotal,
    getCartPriceImpactsTotal,
    isCartEmpty,
    isCartFull,
    getCartPaymentModes,
    canAddAnotherProductToCart,
    initCart,
    resetCart,
    updateCartWithBuyback,
    updateCartWithCustomer,
    updateCartWithPreDeclaredBuybackId,
    removeBuybackFromCart,
    // Temporary customer infos
    loadTempCustomerInfosInCart,
    storeTemporaryCustomerInfos,
    // Cart visibility
    show,
    toggleCartVisibility,
  };
});
